import * as React from 'react';

// useAutoPageSize — measures the grid's scroll container once on mount
// and derives a page size that fits exactly inside it. Resize listeners
// are intentionally NOT wired: re-paginating mid-session as the user
// resizes the window would reshuffle visible rows under the cursor.
// After the initial measure the page size is frozen — if the viewport
// shrinks the table scrolls inside its own container and the sticky
// header stays put.
//
// We measure the SCROLL CONTAINER (the `flex-1` wrapper around the
// `<table>`), not the outer DataGrid container. The flex layout already
// allocated exactly the available vertical space to it after toolbar /
// gaps / footer were laid out, so we only need to subtract the sticky
// `<thead>` to find the body's usable height.
//
// Both the header and a sample row height are read from the rendered
// DOM (via `offsetHeight`) rather than computed from constants — cells
// use rem-based padding (`tw-p-4` = 1rem) and text sizes, so any user
// zoom, accessibility font-size override, or design-token change would
// shift the pixel values out from under a hard-coded estimate. Measuring
// the real DOM removes that whole class of off-by-one bug.
//
// First-mount timing: this hook runs after the consumer's first paint
// (data ready, real rows in the body). The fallback page size of
// `MIN_PAGE_SIZE` is what useReactTable uses on the very first render,
// which is enough rows for the DOM to have a measurable sample row by
// the time the requestAnimationFrame callback runs.

const MIN_PAGE_SIZE = 5;

const useAutoPageSize = (scrollContainerRef, enabled) => {
    const [pageSize, setPageSize] = React.useState(null);

    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        // Wait one frame so the container has its final layout before measuring.
        const frameId = window.requestAnimationFrame(() => {
            const container = scrollContainerRef.current;
            if (!container) {
                return;
            }
            const thead = container.querySelector('thead');
            const sampleRow = container.querySelector('tbody tr');

            if (!thead || !sampleRow) {
                // Body has no measurable row yet (loading / empty state).
                // Bail — the consumer will see MIN_PAGE_SIZE as the resting
                // value, which is the same behaviour as before measurement.
                return;
            }
            const rowHeight = sampleRow.offsetHeight;
            if (rowHeight <= 0) {
                return;
            }
            const available = container.clientHeight - thead.offsetHeight;
            const computed = Math.max(MIN_PAGE_SIZE, Math.floor(available / rowHeight));
            setPageSize(computed);
        });
        return () => {
            window.cancelAnimationFrame(frameId);
        };
        // Measure once when the consumer flips into auto mode; resizes are
        // intentionally ignored.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // Returning null until the first measurement completes lets the caller
    // distinguish "auto, still measuring" from "auto, measured" — the
    // post-measurement render drops `flex-1` from the scroll container so
    // it shrinks to the table's actual height. The caller is responsible
    // for falling back to MIN_PAGE_SIZE during measurement.
    if (!enabled) {
        return null;
    }
    return pageSize;
};

export default useAutoPageSize;
